import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
  Button,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Flag as FlagIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  Work as WorkIcon,
} from '@mui/icons-material';

const TechieSignup: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const countries = [
    {
      code: 'USA',
      name: 'United States',
      flag: '🇺🇸',
      description: 'Sign up for US-based opportunities',
      route: '/techie/ussignup',
      features: [
        'Work Authorization Verification',
        'SSN Document Capture',
        'State ID/Passport Verification',
        'US Employment Opportunities',
        'MM/DD/YYYY Date Format'
      ]
    },
    {
      code: 'INDIA',
      name: 'India',
      flag: '🇮🇳',
      description: 'Sign up for India-based opportunities',
      route: '/techie/indiasignup',
      features: [
        'PAN Card Verification',
        'Aadhaar/DL Verification',
        'Indian Employment Opportunities',
        'Local Compliance Support',
        'DD/MM/YYYY Date Format'
      ]
    }
  ];

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
  };

  const handleContinue = () => {
    if (selectedCountry) {
      const country = countries.find(c => c.code === selectedCountry);
      if (country) {
        navigate(country.route);
      }
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Paper 
            elevation={24} 
            sx={{ 
              p: 4, 
              mb: 4, 
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <IconButton onClick={() => navigate('/signup')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Tech Professional Registration
              </Typography>
            </Box>

            <Box textAlign="center" mb={4}>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                paragraph
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Join VerTechie's verified tech professional network. Choose your country to begin the secure registration process.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                <SecurityIcon color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Your information is protected with enterprise-grade security
                </Typography>
                <VerifiedUserIcon color="success" />
              </Box>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {countries.map((country, index) => (
                <Grid item xs={12} md={6} key={country.code}>
                  <Zoom in timeout={600 + index * 200}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease-in-out',
                        border: selectedCountry === country.code ? '3px solid' : '1px solid',
                        borderColor: selectedCountry === country.code ? 'primary.main' : 'divider',
                        transform: selectedCountry === country.code ? 'scale(1.02)' : 'scale(1)',
                        boxShadow: selectedCountry === country.code 
                          ? '0 8px 32px rgba(33, 150, 243, 0.3)' 
                          : '0 4px 16px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 8px 32px rgba(33, 150, 243, 0.2)',
                        }
                      }}
                      onClick={() => handleCountrySelect(country.code)}
                    >
                      <CardActionArea sx={{ height: '100%', p: 3 }}>
                        <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                            {country.flag}
                          </Typography>
                          
                          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                            {country.name}
                          </Typography>
                          
                          <Typography variant="body1" color="text.secondary" paragraph>
                            {country.description}
                          </Typography>

                          <Box sx={{ mt: 'auto' }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                              Verification Process:
                            </Typography>
                            {country.features.map((feature, idx) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 1,
                                  justifyContent: 'center'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    mr: 1
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={handleContinue}
                disabled={!selectedCountry}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  }
                }}
              >
                Continue Registration
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> All information provided will be verified through official documents and cross-referenced with our verification partners.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default TechieSignup;

